import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Mail, ArrowRight, ShieldCheck, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './LoginPage.css';

const LoginPage = () => {
  const [step, setStep] = useState('email'); // email | code
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl') || '/panel';

  const handleSendCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError('');
    setLoading(true);

    try {
      // TODO: API entegrasyonu — POST /api/Auth/SendMagicLink { email }
      // Şimdilik direkt code adımına geç
      await new Promise(r => setTimeout(r, 800));
      setStep('code');
    } catch {
      setError('Kod gönderilemedi. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setError('');
    setLoading(true);

    try {
      // TODO: API entegrasyonu — POST /api/Auth/VerifyMagicLink { email, code }
      // API'den JWT dönecek → login(token) ile kaydet
      await new Promise(r => setTimeout(r, 800));

      // Simülasyon: gerçek API'de burası response.token olacak
      const mockToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJ1c2VyMSIsImVtYWlsIjoidGVzdEBpc2J1bHN1bi5jb20iLCJuYW1lIjoiVGVzdCBVc2VyIiwiZXhwIjo5OTk5OTk5OTk5fQ.mock';
      login(mockToken);
      navigate(returnUrl, { replace: true });
    } catch {
      setError('Kod geçersiz. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    // TODO: Social login API — window.location.href = API_BASE + `/auth/${provider}?returnUrl=...`
    console.log(`${provider} login, returnUrl: ${returnUrl}`);
  };

  return (
    <div className="login-page">
      <div className="login-page__card">
        <Link to="/" className="login-page__back">
          <ArrowLeft size={16} />
          Ana Sayfa
        </Link>

        <div className="login-page__header">
          <h1 className="login-page__title">
            {step === 'email' ? 'Giriş Yap' : 'Kodu Gir'}
          </h1>
          <p className="login-page__desc">
            {step === 'email'
              ? 'E-posta adresine giriş kodu göndereceğiz.'
              : <>Kodu <strong>{email}</strong> adresine gönderdik.</>
            }
          </p>
        </div>

        {step === 'email' ? (
          <>
            {/* Social Login */}
            <div className="login-page__socials">
              <button className="login-page__social" onClick={() => handleSocialLogin('google')}>
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Google ile devam et
              </button>
              <button className="login-page__social" onClick={() => handleSocialLogin('linkedin')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#0A66C2">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                LinkedIn ile devam et
              </button>
            </div>

            <div className="login-page__divider"><span>veya</span></div>

            {/* Email form */}
            <form onSubmit={handleSendCode} className="login-page__form">
              <div className="login-page__input-wrap">
                <Mail size={18} className="login-page__input-icon" />
                <input
                  type="email"
                  className="login-page__input"
                  placeholder="E-posta adresiniz"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              {error && <p className="login-page__error">{error}</p>}
              <button type="submit" className="login-page__submit" disabled={loading}>
                {loading ? 'Gönderiliyor...' : <>Kod Gönder <ArrowRight size={16} /></>}
              </button>
            </form>
          </>
        ) : (
          <>
            {/* Code verification */}
            <form onSubmit={handleVerifyCode} className="login-page__form">
              <div className="login-page__input-wrap">
                <ShieldCheck size={18} className="login-page__input-icon" />
                <input
                  type="text"
                  className="login-page__input"
                  placeholder="6 haneli kod"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  maxLength={6}
                  required
                  autoFocus
                />
              </div>
              {error && <p className="login-page__error">{error}</p>}
              <button type="submit" className="login-page__submit" disabled={loading}>
                {loading ? 'Doğrulanıyor...' : <>Giriş Yap <ArrowRight size={16} /></>}
              </button>
            </form>
            <button className="login-page__resend" onClick={() => { setStep('email'); setCode(''); setError(''); }}>
              Farklı e-posta ile dene
            </button>
          </>
        )}

        <p className="login-page__hint">
          Şifre gerektirmez. E-postana gelen kodu girerek giriş yap.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
